// src/pages/stylist/AvailabilityPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useAppSelector } from '../../hooks/useRedux';
import { scheduleService, WorkingHours } from '../../services/scheduleService';
import TimePickerModal from '../../components/schedule/TimePickerModal';
import '../../styles/TimePicker.css';
import '../../styles/Availability.css';

// Days of the week in order
const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const AvailabilityPage: React.FC = () => {
  const { user } = useAppSelector(state => state.auth);
  
  // Component state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [timePickerVisible, setTimePickerVisible] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedShiftIndex, setSelectedShiftIndex] = useState<number>(-1);
  const [isEditingStart, setIsEditingStart] = useState(false);
  
  // Initial schedule state with all days
  const initialSchedule: WorkingHours[] = DAYS_OF_WEEK.map(day => ({
    day,
    isWorkingDay: false,
    shifts: []
  }));
  
  const [schedule, setSchedule] = useState<WorkingHours[]>(initialSchedule);
  const savingRef = useRef(false);
  
  // Fetch schedule data on component mount
  useEffect(() => {
    fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // fetchSchedule is stable
  
  // Fetch stylist's schedule data
  const fetchSchedule = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await scheduleService.getStylistSchedule(user.id);
      // getStylistSchedule returns { defaultHours: [...], specialDates: [...], ... }
      let defaultHours = response.defaultHours || [];
      
      console.log('[WEB AVAILABILITY] Fetched defaultHours:', JSON.stringify(defaultHours, null, 2));
      
      // Ensure all days of the week are present, but NEVER override user preferences
      const scheduleMap = new Map(defaultHours.map(day => [day.day, day]));
      
      // Check if any days are missing
      const missingDays = DAYS_OF_WEEK.filter(day => !scheduleMap.has(day));
      
      if (missingDays.length > 0) {
        console.log(`[WEB AVAILABILITY] Found ${missingDays.length} missing days: ${missingDays.join(', ')}`);
        const workingDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        
        // Only add missing days with defaults, preserve ALL existing days exactly as-is
        const completeSchedule = DAYS_OF_WEEK.map(day => {
          const existing = scheduleMap.get(day);
          
          if (existing) {
            // CRITICAL: Return existing exactly as-is - never modify user preferences
            return existing;
          }
          
          // Day is missing, create it with defaults
          const isWorkingDay = workingDays.includes(day);
          return {
            day,
            isWorkingDay,
            shifts: isWorkingDay ? [{ startTime: '09:00', endTime: '17:00' }] : []
          };
        });
        
        setSchedule(completeSchedule);
      } else {
        // All days present - use schedule as-is without any modifications
        console.log('[WEB AVAILABILITY] All 7 days present, using schedule without modifications');
        setSchedule(defaultHours);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching schedule:', error);
      setError('Failed to load schedule data. Please try again.');
      setLoading(false);
    }
  };
  
  // Toggle working day status
  const handleToggleWorkingDay = async (day: string, currentValue: boolean) => {
    if (savingRef.current) return;
    try {
      savingRef.current = true;
      const updatedSchedule = schedule.map(daySchedule => {
        if (daySchedule.day === day) {
          // Create a default shift for newly working days
          const newShifts = !currentValue 
            ? [{ startTime: '09:00', endTime: '17:00' }] 
            : [];
          
          return {
            ...daySchedule,
            isWorkingDay: !currentValue,
            shifts: newShifts
          };
        }
        return daySchedule;
      });
      
      setLoading(true);
      await scheduleService.updateWorkingHours(updatedSchedule);
      setSchedule(updatedSchedule);
      setSuccess('Schedule updated successfully');
      setLoading(false);
    } catch (error) {
      console.error('Error updating schedule:', error);
      setError('Failed to update schedule. Please try again.');
      setLoading(false);
    } finally {
      savingRef.current = false;
    }
  };
  
  // Add a new shift to a day
  const handleAddShift = async (day: string) => {
    if (savingRef.current) return;
    const daySchedule = schedule.find(d => d.day === day);
    if (!daySchedule) return;
    
    // Create a new shift with properties that match WorkingHours.shifts
    const newShift = { 
      startTime: '09:00', 
      endTime: '17:00'
    };
    
    const updatedSchedule = schedule.map(daySchedule => {
      if (daySchedule.day === day) {
        return {
          ...daySchedule,
          shifts: [
            ...daySchedule.shifts,
            newShift
          ]
        };
      }
      return daySchedule;
    });
    
    try {
      savingRef.current = true;
      setLoading(true);
      await scheduleService.updateWorkingHours(updatedSchedule);
      setSchedule(updatedSchedule);
      setSuccess('Shift added successfully');
      setLoading(false);
    } catch (error) {
      console.error('Error adding shift:', error);
      setError('Failed to add shift. Please try again.');
      setLoading(false);
    } finally {
      savingRef.current = false;
    }
  };
  
  // Remove a shift
  const handleRemoveShift = async (day: string, shiftIndex: number) => {
    if (savingRef.current) return;
    if (!window.confirm('Are you sure you want to remove this shift?')) {
      return;
    }
    
    const updatedSchedule = schedule.map(daySchedule => {
      if (daySchedule.day === day) {
        const newShifts = [...daySchedule.shifts];
        newShifts.splice(shiftIndex, 1);
        return { ...daySchedule, shifts: newShifts };
      }
      return daySchedule;
    });
    
    try {
      savingRef.current = true;
      setLoading(true);
      await scheduleService.updateWorkingHours(updatedSchedule);
      setSchedule(updatedSchedule);
      setSuccess('Shift removed successfully');
      setLoading(false);
    } catch (error) {
      console.error('Error removing shift:', error);
      setError('Failed to remove shift. Please try again.');
      setLoading(false);
    } finally {
      savingRef.current = false;
    }
  };
  
  // Select time for editing
  const handleTimeSelect = (day: string, shiftIndex: number, isStart: boolean) => {
    setSelectedDay(day);
    setSelectedShiftIndex(shiftIndex);
    setIsEditingStart(isStart);
    setTimePickerVisible(true);
  };
  
  // Handle time picker confirmation
  const handleTimeConfirm = async (time: string) => {
    if (!selectedDay || selectedShiftIndex === -1 || savingRef.current) return;
    
    const updatedSchedule = schedule.map(daySchedule => {
      if (daySchedule.day === selectedDay) {
        const newShifts = [...daySchedule.shifts];
        if (isEditingStart) {
          newShifts[selectedShiftIndex].startTime = time;
        } else {
          newShifts[selectedShiftIndex].endTime = time;
        }
        return { ...daySchedule, shifts: newShifts };
      }
      return daySchedule;
    });
    
    try {
      savingRef.current = true;
      setLoading(true);
      await scheduleService.updateWorkingHours(updatedSchedule);
      setSchedule(updatedSchedule);
      setSuccess('Schedule updated successfully');
      setLoading(false);
    } catch (error) {
      console.error('Error updating time:', error);
      setError('Failed to update time. Please try again.');
      setLoading(false);
    } finally {
      savingRef.current = false;
    }
    
    setTimePickerVisible(false);
    setSelectedDay(null);
    setSelectedShiftIndex(-1);
  };
  
  // Clear notifications
  const clearNotification = () => {
    setError(null);
    setSuccess(null);
  };
  
  return (
    <div className="availability-page">
      <div className="availability-header">
        <h1 className="availability-title">Manage Availability</h1>
        <p className="availability-subtitle">Set your working hours, shifts, and manage your schedule.</p>
      </div>
      
      {/* Notifications */}
      {error && (
        <div className="notification notification--error">
          <span>{error}</span>
          <button className="notification__close" onClick={clearNotification}>×</button>
        </div>
      )}
      
      {success && (
        <div className="notification notification--success">
          <span>{success}</span>
          <button className="notification__close" onClick={clearNotification}>×</button>
        </div>
      )}
      
      {/* Loading State */}
      {loading && (
        <div className="availability-loading">
          <div className="loader"></div>
          <p>Loading...</p>
        </div>
      )}
      
      {/* Working Hours Section */}
      <div className="availability-card">
        <div className="availability-card__header">
          <h2 className="availability-card__title">Working Hours</h2>
        </div>
        
        <div className="availability-card__content">
          {DAYS_OF_WEEK.map((day) => {
            const daySchedule = schedule.find(d => d.day === day) || {
              day,
              isWorkingDay: false,
              shifts: []
            };
            
            return (
              <div key={day} className="day-container">
                <div className="day-header">
                  <span className="day-name">{day}</span>
                  
                  <div className="day-toggle">
                    <label className="switch">
                      <input 
                        type="checkbox" 
                        checked={daySchedule.isWorkingDay}
                        onChange={() => handleToggleWorkingDay(day, daySchedule.isWorkingDay)}
                      />
                      <span className="slider round"></span>
                    </label>
                    <span className="toggle-label">
                      {daySchedule.isWorkingDay ? 'Working' : 'Off'}
                    </span>
                  </div>
                </div>
                
                {daySchedule.isWorkingDay && (
                  <div className="shifts-container">
                    {daySchedule.shifts.map((shift, index) => (
                      <div key={index} className="shift-row">
                        <button 
                          className="time-button" 
                          onClick={() => handleTimeSelect(day, index, true)}
                        >
                          {shift.startTime}
                        </button>
                        
                        <span className="time-separator">to</span>
                        
                        <button 
                          className="time-button" 
                          onClick={() => handleTimeSelect(day, index, false)}
                        >
                          {shift.endTime}
                        </button>
                        
                        <button 
                          className="remove-button" 
                          onClick={() => handleRemoveShift(day, index)}
                          aria-label="Remove shift"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6H5H21" stroke="#C0392B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#C0392B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                    
                    <button 
                      className="add-shift-button" 
                      onClick={() => handleAddShift(day)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19" stroke="#3E6AE1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5 12H19" stroke="#3E6AE1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Add Shift</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Special Dates & Time Off Section */}
      <div className="availability-card">
        <div className="availability-card__header">
          <h2 className="availability-card__title">Time Off & Special Dates</h2>
        </div>
        
        <div className="availability-card__content">
          <div className="coming-soon">
            <p>Time off management and special dates functionality will be available soon.</p>
            <button className="tesla-button tesla-button--outline" disabled>
              Add Time Off
            </button>
          </div>
        </div>
      </div>
      
      {/* Time Picker Modal */}
      <TimePickerModal
        visible={timePickerVisible}
        onCancel={() => setTimePickerVisible(false)}
        onConfirm={handleTimeConfirm}
        initialTime={
          selectedDay && selectedShiftIndex !== -1 && schedule.find(d => d.day === selectedDay)?.shifts[selectedShiftIndex]
            ? isEditingStart
              ? schedule.find(d => d.day === selectedDay)!.shifts[selectedShiftIndex].startTime
              : schedule.find(d => d.day === selectedDay)!.shifts[selectedShiftIndex].endTime
            : '09:00'
        }
      />
    </div>
  );
};

export default AvailabilityPage;